import _get from 'lodash/get';
import _memoize from 'lodash/memoize';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { getScreenSize } from '../utils/sizes';
import Poster from '../poster/poster.component';
import Portrait from '../portrait/portrait.component';
import Jacket from '../jacket/jacket.component';
import Card from '../card/card.component';
import Totem from '../totem/totem.component';
import Cover from '../cover/cover.component';
import Hero from '../hero/hero.component';
import Solo from '../solo/solo.component';
import Jumbotron from '../jumbotron/jumbotron.component';
import JumbotronSquare from '../jumbotron-square/jumbotronSquare.component';
import HorizontalCard from '../horizontal-card/horizontalCard.component';
import HorizontalSquare from '../horizontal-square/horizontalSquare.component';
import Square from '../square/square.component';
import BigSquare from '../bigSquare/bigSquare.component';
import FlatRectangle from '../flatRectangle/flatRectangle.component';
import Banner from '../banner/banner.component';
import AlertView from '../alert-view/AlertView.component';
import SquareMeta from '../square-meta/squareMeta.component';

export const contentFallback = Jacket.Templates.Jacket;

export const contentTemplates = {
  [Portrait.Templates.Portrait]: Portrait,
  [Poster.Templates.PosterS]: Poster,
  [Poster.Templates.PosterM]: Poster,
  [Poster.Templates.PosterL]: Poster,
  [Jacket.Templates.Jacket]: Jacket,
  [Card.Templates.CardS]: Card,
  [Card.Templates.CardM]: Card,
  [Card.Templates.CardL]: Card,
  [Totem.Templates.Totem]: Totem,
  [Cover.Templates.Cover]: Cover,
  [Hero.Templates.Hero]: Hero,
  [Solo.Templates.Solo]: Solo,
  [Jumbotron.Templates.Jumbotron]: Jumbotron,
  [JumbotronSquare.Templates.JumbotronSquare]: JumbotronSquare,
  [HorizontalCard.Templates.HorizontalCard]: HorizontalCard,
  [HorizontalSquare.Templates.HorizontalSquare]: HorizontalSquare,
  [Square.Templates.Square]: Square,
  [BigSquare.Templates.BigSquare]: BigSquare,
  [FlatRectangle.Templates.FlatRectangle]: FlatRectangle,
  [Banner.Templates.Banner]: Banner,
  [AlertView.Templates.AlertView]: AlertView,
  [SquareMeta.Templates.SquareMeta]: SquareMeta,
};

export const getContentTemplateByTemplateId = templateId =>
  contentTemplates[templateId] || contentTemplates[contentFallback];

/**
 * Returns item sizes for every context a template defines, keyed by context name
 *
 * @param {string} contentTemplateId identifier of a content template
 * @returns {Object} a `{ [context]: { [screenSize]: { w, h } } }` map
 */
export const getContentSizeContextsByContentTemplateId = contentTemplateId => {
  const contentTemplate = getContentTemplateByTemplateId(contentTemplateId);

  return _get(contentTemplate, `Sizes.${contentTemplateId}`) || _get(contentTemplate, `Sizes.${contentFallback}`);
};

/**
 * Returns the item size of a template in a given rendering context for the current screen size,
 * falling back to the default context when the template does not define it
 *
 * @param {string} contentTemplateId identifier of a content template
 * @param {string} context one of RENDERING_CONTEXT values
 * @returns {Object} a `{ w, h }` item size
 */
export const getContentSizeByContentTemplateId = (contentTemplateId, context = RENDERING_CONTEXT.GENERAL) => {
  const sizeContexts = getContentSizeContextsByContentTemplateId(contentTemplateId);
  const sizes = sizeContexts[context] || sizeContexts[RENDERING_CONTEXT.GENERAL];

  return sizes[getScreenSize()] || sizes;
};

/**
 * Returns image options for every context a template defines, keyed by context name
 *
 * @param {string} contentTemplateId identifier of a content template
 * @returns {Object} a `{ [context]: { [screenSize]: ImageOptions } }` map
 */
export const getImageOptionsContextsByContentTemplateId = contentTemplateId => {
  const contentTemplate = getContentTemplateByTemplateId(contentTemplateId);

  return (
    _get(contentTemplate, `ImageOptions.${contentTemplateId}`) ||
    _get(contentTemplate, `ImageOptions.${contentFallback}`)
  );
};

/**
 * Returns image options for a template in a given rendering context,
 * falling back to the default context when the template does not define it
 *
 * @param {string} contentTemplateId identifier of a content template
 * @param {string} context one of RENDERING_CONTEXT values
 * @returns {Object} a `{ [screenSize]: ImageOptions }` map
 */
export const getImageOptionsByContentTemplateId = (contentTemplateId, context = RENDERING_CONTEXT.GENERAL) => {
  const imageOptionsContexts = getImageOptionsContextsByContentTemplateId(contentTemplateId);

  return imageOptionsContexts[context] || imageOptionsContexts[RENDERING_CONTEXT.GENERAL];
};

export const getContentTemplateOptions = _memoize(() =>
  Object.keys(contentTemplates).reduce(
    (result, key) => ({
      ...result,
      [key]: {
        imageOptions: getImageOptionsContextsByContentTemplateId(key),
        size: getContentSizeByContentTemplateId(key),
      },
    }),
    {},
  ),
);
